import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ScavengerItem, Submission } from '../types';
import { scavengerService, imageService } from '../services/api';
import { convertHeicToJpeg, compressImage } from '../utils/imageCompression';
import './ItemCard.css';



interface ItemCardProps {
  item: ScavengerItem;
  submission?: Submission;
  onImageSubmit: (itemId: number, file: File) => Promise<void>;
  onImageDelete?: () => void;
  submissionsEnabled?: boolean;
  scoresReleased?: boolean;
}

const ItemCard: React.FC<ItemCardProps> = ({ item, submission, onImageSubmit, onImageDelete, submissionsEnabled = true, scoresReleased = true }) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [imageError, setImageError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Basic file validation
    const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || 
                   file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
    
    if (!file.type.startsWith('image/') && !isHeic) {
      setError('Please select an image file (including HEIC/HEIF)');
      return;
    }

    // Validate file size (max 10MB before processing)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setError('');
      setUploading(true);
      
      // Step 1: Convert HEIC to JPEG if needed
      let processedFile = await convertHeicToJpeg(file);
      
      // Step 2: Compress the image
      processedFile = await compressImage(processedFile, 1200, 0.8);
      
      console.log(`File processing complete: ${file.size} → ${processedFile.size} bytes (${Math.round((1 - processedFile.size / file.size) * 100)}% reduction)`);
      
      await onImageSubmit(item.id, processedFile);
    } catch (error: any) {
      setError(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleImageClick = () => {
    setShowModal(true);
  };

  const handleReupload = () => {
    if (fileInputRef.current && !uploading) {
      fileInputRef.current.click();
      setShowModal(false);
    }
  };

  const handleDelete = async () => {
    if (!submission || deleting || uploading) return;

    const confirmed = window.confirm('Are you sure you want to delete this image? This action cannot be undone.');
    if (!confirmed) return;

    try {
      setDeleting(true);
      setError('');
      
      await scavengerService.deleteImage(item.id);
      
      // Call onImageDelete to refresh the data
      onImageDelete?.();
      setShowModal(false);
    } catch (error: any) {
      console.error('Delete failed:', error);
      setError(error.response?.data?.error || 'Failed to delete image');
    } finally {
      setDeleting(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const isCompleted = !!submission;

  // Reset image error when submission changes
  useEffect(() => {
    setImageError(false);
  }, [submission?.imagePath]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showModal) {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset'; // Restore scrolling
    };
  }, [showModal]);

  return (
    <div className={`item-card ${isCompleted ? 'completed' : 'pending'}`}>
      <div className="item-header">
        <div className="item-number">#{item.id}</div>
        <div className="item-score">{item.score} pts</div>
      </div>

      <div className="item-description">
        {item.description}
      </div>

      {isCompleted ? (
        <div className="submission-display">
          {!imageError && submission?.imagePath ? (
            <div className="clickable-image-container" onClick={handleImageClick}>
              <img 
                src={imageService.getImageUrl(submission.imagePath)}
                alt={`Submission for ${item.description}`}
                className="submission-image"
                loading="lazy"
                onLoad={() => {
                  console.log('Image loaded successfully:', submission.imagePath);
                }}
                onError={(e) => {
                  console.error('Failed to load image:', submission.imagePath);
                  console.error('Image URL:', imageService.getImageUrl(submission.imagePath));
                  console.error('Error event:', e);
                  setImageError(true);
                }}
              />
              <div className="image-overlay-text">Click to view full size</div>
            </div>
          ) : (
            <div className="image-error">
              <p>Failed to load image</p>
              <small>{submission?.imagePath || 'No image path'}</small>
              <button 
                onClick={() => setImageError(false)}
                style={{ marginTop: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
              >
                Retry
              </button>
            </div>
          )}
          <div className="submission-info">
            <span className={`submission-status ${scoresReleased ? submission.status : 'pending'}`}>
              {scoresReleased 
                ? (submission.status === 'correct' ? '✓ Approved' :
                   submission.status === 'wrong' ? '✗ Rejected' :
                   '? Under Review')
                : '📋 Submitted'
              }
            </span>
            <span className="submission-date">
              {new Date(submission.timestamp).toLocaleDateString()}
            </span>
          </div>
          
          {uploading && (
            <div className="upload-status">
              <span>Replacing image...</span>
            </div>
          )}
          
          {error && <div className="upload-error">{error}</div>}
        </div>
      ) : (
        <div className="upload-section">
          {submissionsEnabled ? (
            <>
              <label className={`upload-button ${uploading ? 'uploading' : ''}`}>
                {uploading ? 'Uploading...' : 'Upload Photo'}
                <input
                  type="file"
                  accept="image/*,.heic,.heif"
                  onChange={handleFileChange}
                  disabled={uploading}
                  className="file-input"
                />
              </label>
              
              {error && <div className="upload-error">{error}</div>}
            </>
          ) : (
            <div className="upload-disabled">
              <span>🚫 Submissions Disabled</span>
              <p>The Huntmaster has temporarily disabled new submissions.</p>
            </div>
          )}
        </div>
      )}

      {/* Image Modal using Portal */}
      {showModal && submission?.imagePath && ReactDOM.createPortal(
        <div className="image-modal-overlay" onClick={handleModalClose}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="image-modal-header">
              <h3>Item #{item.id}: {item.description}</h3>
              <button className="modal-close-button" onClick={handleModalClose}>×</button>
            </div>
            
            <div className="image-modal-body">
              <img 
                src={imageService.getImageUrl(submission.imagePath)}
                alt={`Full size submission for ${item.description}`}
                className="modal-image"
                onError={(e) => {
                  console.error('Failed to load modal image:', submission.imagePath);
                  setImageError(true);
                  setShowModal(false);
                }}
              />
            </div>
            
            <div className="image-modal-footer">
              <div className="modal-buttons">
                <button 
                  className="reupload-button"
                  onClick={handleReupload}
                  disabled={uploading || deleting || !submissionsEnabled}
                  title={!submissionsEnabled ? 'Submissions are currently disabled' : undefined}
                >
                  {uploading ? 'Uploading...' : 'Reupload Image'}
                </button>
                <button 
                  className="delete-button"
                  onClick={handleDelete}
                  disabled={uploading || deleting || !submissionsEnabled}
                  title={!submissionsEnabled ? 'Submissions are currently disabled' : undefined}
                >
                  {deleting ? 'Deleting...' : 'Delete Image'}
                </button>
              </div>
              <span className="submission-date">
                Uploaded: {new Date(submission.timestamp).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Hidden file input for replacing images */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        onChange={handleFileChange}
        disabled={uploading}
        className="file-input"
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default ItemCard;
